import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DayOfWeek } from './types';

/**
 * Before getting carried away converting Dayjs to Moment because it is what you've always used,
 * please read their homepage, which details why you shouldn't use it.
 * Dayjs provides a nearly identical, much faster, and more modern API.
 * The docs for Dayjs are also nearly identical to Moment.
 *
 * Cheers, Ben
 *
 * https://momentjs.com/docs/#/-project-status/
 */

dayjs.extend(utc);
dayjs.extend(timezone);

export const DAYS_OF_WEEK: DayOfWeek[] = [
  { fullLabel: 'Sunday', label: 'Sun', value: 0, isWeekday: false, shortLabel: 'Su' },
  { fullLabel: 'Monday', label: 'Mon', value: 1, isWeekday: true, shortLabel: 'M' },
  { fullLabel: 'Tuesday', label: 'Tue', value: 2, isWeekday: true, shortLabel: 'T' },
  { fullLabel: 'Wednesday', label: 'Wed', value: 3, isWeekday: true, shortLabel: 'W' },
  { fullLabel: 'Thursday', label: 'Thu', value: 4, isWeekday: true, shortLabel: 'R' },
  { fullLabel: 'Friday', label: 'Fri', value: 5, isWeekday: true, shortLabel: 'F' },
  { fullLabel: 'Saturday', label: 'Sat', value: 6, isWeekday: false, shortLabel: 'Sa' },
];

/**
 * Splits a date range into day-long chunks.
 * @param start The start date (ISO8601 string OR dayjs object)
 * @param end The end date (ISO8601 string OR dayjs object)
 * @returns {[]} An array of date range arrays, [ISO start, ISO end]
 */
export const getDatesBetween = (start: string | Dayjs, end: string | Dayjs) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const startDate = dayjs.tz(start, tz);
  const endDate = dayjs.tz(end, tz);

  const dateArray: string[][] = [];
  const timeBetween = endDate.diff(startDate, 'hours');
  // We're only selecting one chunk, so just leave it as-is to save spinning up an instance
  if (timeBetween <= 24) {
    dateArray.push([startDate.toISOString(), endDate.toISOString()]);
    return dateArray;
  } else {
    // If the time isn't in UTC already, we need to add an extra split for the left-over
    const splits = timeBetween / 24 + ((!startDate.isUTC() ? 1 : 0) || 0);
    // Iterate once per split
    for (let i = 0; i < splits; i++) {
      // Add i days
      let splitStartDate = startDate.clone().add(i, 'days').utc().startOf('day');
      let splitEndDate = splitStartDate.utc().endOf('day');
      if (splitStartDate.isBefore(startDate)) splitStartDate = startDate;
      if (splitEndDate.isAfter(endDate)) splitEndDate = endDate;

      dateArray.push([splitStartDate.toISOString(), splitEndDate.toISOString()]);
    }
  }
  return dateArray;
};

/**
 * Converts a number of minutes since 00:00 into a 24hr time
 * @param minutes
 */
export const minutesAsTime = (minutes: number): string => {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

/**
 * Converts a 24hr time (string) into a number of minutes since 00:00
 * @param time
 */
export const timeAsMinutes = (time: string): number => {
  const matched = time.match(/(\d{2}):(\d{2})/);
  if (!matched) return 0;
  return (parseInt(matched[1]) * 60) + (parseInt(matched[2]) * 60);
};

/**
 * Converts a Dayjs object to minutes since midnight, UTC
 * @param dayjsObj
 */
export const dayjsAsMinutes = (dayjsObj: Dayjs): number => (dayjsObj.utc().hour() * 60) + dayjsObj.utc().minute();

/**
 * Given a number of minutes since midnight in local time, provide the number of minutes since midnight UTC
 * @param minute
 */
export const adjustLocalMinuteToUtc = (minute: number): number => {
  const offset = new Date().getTimezoneOffset();
  let adjustedTime = minute + offset;
  if (adjustedTime < 0) adjustedTime = 1440 - (0 - adjustedTime);
  else if (adjustedTime > 1440) adjustedTime = (adjustedTime - 1440);
  return adjustedTime;
};

/**
 * Given a number of minutes since midnight in UTC, provide the number of minutes since midnight local time
 * @param minute
 */
export const adjustUtcMinuteToLocal = (minute: number): number => {
  const offset = new Date().getTimezoneOffset();
  let adjustedTime = minute - offset;
  if (adjustedTime < 0) adjustedTime = 1440 - (0 - adjustedTime);
  else if (adjustedTime > 1440) adjustedTime = (adjustedTime - 1440);
  return adjustedTime;
};
